// Globals
import debounce from 'lodash.debounce';
import { ChangeEvent, memo, ReactNode, useMemo } from 'react';
import { useParams } from 'react-router-dom';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { getFilterValues, selectFiltersValuesQuery } from '../../feature';
import { changeFilterValuesQuery } from '../../feature/slice';

// Components
import { Input } from '@/ui';
import { StyledFilterRow } from './FilterValuesFiltersContainerStyles';

// Modules
import { Resource, usePermissions } from '@/modules/Permissions';

// Helpers
import { GetFilterValuesDTO } from '../../dtos';
import { FiltersValuesFieldNames } from '../../helpers/types';

type IFilterValuesFiltersContainer = {
  children?: ReactNode;
  className?: string;
};

const FilterValuesFiltersContainer = ({ children, className }: IFilterValuesFiltersContainer) => {
  const dispatch = useAppDispatch();
  const query = useAppState(selectFiltersValuesQuery);
  const { filterId } = useParams();
  const { canManage } = usePermissions();
  // The trailing actions column only exists when the admin can manage filters.
  const canManageFilters = canManage(Resource.FILTER);

  const handleDebounceSearch = useMemo(
    () =>
      debounce(async (searchParams) => {
        const dto = new GetFilterValuesDTO({ ...searchParams, filter_id: filterId });
        dispatch(getFilterValues(dto));
      }, 500),
    [dispatch, filterId],
  );

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const newQuery = { ...query, [name]: value || undefined };
    dispatch(changeFilterValuesQuery(newQuery));
    handleDebounceSearch(newQuery);
  };

  return (
    <thead className={className}>
      {children}
      <StyledFilterRow>
        <th>
          <Input
            name={FiltersValuesFieldNames.TITLE_UK}
            onChange={handleSearch}
            value={query?.[FiltersValuesFieldNames.TITLE_UK] || ''}
            size="large"
            allowClear
          />
        </th>
        <th>
          <Input
            name={FiltersValuesFieldNames.TITLE_RU}
            onChange={handleSearch}
            value={query?.[FiltersValuesFieldNames.TITLE_RU] || ''}
            size="large"
            allowClear
          />
        </th>
        <th>
          <Input
            name={FiltersValuesFieldNames.SLUG}
            onChange={handleSearch}
            value={query?.slug || ''}
            size="large"
            allowClear
          />
        </th>
        {canManageFilters && <th />}
      </StyledFilterRow>
    </thead>
  );
};

export default memo(FilterValuesFiltersContainer);
